import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { inject, Injectable, PLATFORM_ID, signal, WritableSignal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { environment } from '../../environments/environment';

export type AnalyticsConsent = 'granted' | 'denied';

export type AnalyticsEventName =
  'login' | 'logout' | 'upload_start' | 'upload_complete' | 'upload_failed' | 'form_error';

type AnalyticsParams = Record<string, string | number | boolean | undefined>;

type StoredConsent = {
  value: AnalyticsConsent;
  date: number;
};

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
  }
}

const CONSENT_STORAGE_KEY: string = 'analytics_consent';
const LOGIN_PENDING_KEY: string = 'analytics_login_pending';
/** Durée de conservation du choix recommandée par la CNIL (6 mois) */
const CONSENT_MAX_AGE_MS: number = 1000 * 60 * 60 * 24 * 182;
/** Préfixe des cookies GA : évite tout conflit avec les cookies `_ga` du site principal */
const COOKIE_PREFIX: string = 'creator';

/**
 * Google Analytics 4, une propriété par environnement (`gAnalyticsCode`).
 * Le script gtag.js n'est chargé qu'après consentement explicite.
 * Sans `gAnalyticsCode`, le service est inactif (pas de bandeau, pas d'envoi).
 */
@Injectable({
  providedIn: 'root',
})
export class AnalyticsService {
  private readonly router: Router = inject(Router);
  private readonly document: Document = inject(DOCUMENT);
  private readonly measurementId: string = environment.gAnalyticsCode;
  private loaded: boolean = false;

  readonly enabled: boolean = isPlatformBrowser(inject(PLATFORM_ID)) && !!this.measurementId;

  /** Choix de l'utilisateur, null tant qu'il n'a pas répondu au bandeau */
  readonly consent: WritableSignal<AnalyticsConsent | null> = signal(this.readStoredConsent());

  init(): void {
    if (!this.enabled) return;

    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      // Le Router met à jour document.title juste après NavigationEnd : on attend la fin du tick
      .subscribe((event: NavigationEnd) =>
        setTimeout(() => this.trackPageView(event.urlAfterRedirects)),
      );

    if (this.consent() === 'granted') this.load();
  }

  setConsent(value: AnalyticsConsent): void {
    if (!this.enabled) return;

    const stored: StoredConsent = { value, date: Date.now() };
    localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(stored));
    this.consent.set(value);

    if (value === 'granted') {
      if (this.loaded) {
        window.gtag('consent', 'update', { analytics_storage: 'granted' });
      } else {
        this.load();
      }
      this.trackPageView(this.router.url);
    } else if (this.loaded) {
      window.gtag('consent', 'update', { analytics_storage: 'denied' });
      this.deleteGaCookies();
    }
  }

  /** Réaffiche le bandeau pour permettre de modifier son choix */
  reviewConsent(): void {
    if (!this.enabled) return;
    this.consent.set(null);
  }

  /** La connexion recharge la page : l'événement `login` est envoyé au chargement suivant */
  markLoginPending(): void {
    if (!this.enabled) return;
    sessionStorage.setItem(LOGIN_PENDING_KEY, '1');
  }

  track(name: AnalyticsEventName, params: AnalyticsParams = {}): void {
    if (!this.loaded || this.consent() !== 'granted') return;
    window.gtag('event', name, params);
  }

  private load(): void {
    if (this.loaded) return;
    this.loaded = true;

    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag(): void {
      // gtag.js attend l'objet `arguments`, pas un tableau
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer.push(arguments);
    };

    window.gtag('consent', 'default', {
      analytics_storage: 'granted',
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
    });
    window.gtag('js', new Date());
    window.gtag('config', this.measurementId, {
      send_page_view: false,
      cookie_prefix: COOKIE_PREFIX,
      page_location: this.sanitizeUrl(window.location.href),
    });

    const script: HTMLScriptElement = this.document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${this.measurementId}`;
    this.document.head.appendChild(script);

    if (sessionStorage.getItem(LOGIN_PENDING_KEY)) {
      sessionStorage.removeItem(LOGIN_PENDING_KEY);
      this.track('login', { method: 'kinomap_oauth' });
    }
  }

  private trackPageView(url: string): void {
    if (!this.loaded || this.consent() !== 'granted') return;
    const pageLocation: string = this.sanitizeUrl(url);
    // `set` pour que les événements suivants héritent aussi de l'URL nettoyée
    window.gtag('set', { page_location: pageLocation, page_title: this.document.title });
    window.gtag('event', 'page_view');
  }

  /** Retire le token OAuth et le hash pour ne jamais les envoyer à Google */
  private sanitizeUrl(url: string): string {
    const parsed: URL = new URL(url, window.location.origin);
    parsed.searchParams.delete('token');
    parsed.hash = '';
    return parsed.toString();
  }

  private readStoredConsent(): AnalyticsConsent | null {
    try {
      const raw: string | null = localStorage.getItem(CONSENT_STORAGE_KEY);
      if (!raw) return null;
      const stored: StoredConsent = JSON.parse(raw);
      if (Date.now() - stored.date > CONSENT_MAX_AGE_MS) return null;
      return stored.value === 'granted' || stored.value === 'denied' ? stored.value : null;
    } catch {
      return null;
    }
  }

  private deleteGaCookies(): void {
    const hostParts: string[] = window.location.hostname.split('.');
    const domains: string[] = hostParts.map((_: string, index: number) =>
      hostParts.slice(index).join('.'),
    );
    this.document.cookie
      .split(';')
      .map((cookie: string) => cookie.split('=')[0].trim())
      .filter((name: string) => name.startsWith(`${COOKIE_PREFIX}_ga`))
      .forEach((name: string) => {
        const expired: string = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
        this.document.cookie = expired;
        domains.forEach(
          (domain: string) => (this.document.cookie = `${expired}; domain=.${domain}`),
        );
      });
  }
}
